import { useController } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import { toKebabCase } from "@miplatform/utils";
import {
  FormControl,
  FormHelperText,
  MenuItem,
  TextField,
} from "@mui/material";

const useStyles = makeStyles()(() => ({
  selectBox: { width: "100%" },
}));

/**
 * @typedef {Object} SelectOption
 * @property {string|number|null} value - The value of the option
 * @property {string} label - The display label for the option
 * @property {boolean} [disabled] - Whether the option is disabled
 */

/**
 * A controlled select box component integrated with React Hook Form
 *
 * @param {Object} props - The component props
 * @param {string} props.name - The name of the form field (required for React Hook Form)
 * @param {Object} [props.formControl] - React Hook Form control object
 * @param {string} props.label - The label text for the select field
 * @param {string} [props.fieldType="select"] - The type of field (typically "select")
 * @param {Object} [props.rules] - Validation rules for React Hook Form
 * @param {SelectOption[]} [props.options] - Array of option objects with value, label, and optional disabled properties
 * @param {boolean} [props.required] - Whether the field is required
 * @param {boolean} [props.disabled] - Whether the select is disabled
 * @param {boolean} [props.disableNone] - Whether to hide the "None" option
 * @param {Function} [props.renderOption] - Custom render function for options
 * @param {Function} [props.handleChange] - Custom change handler (overrides default onChange)
 * @param {string|number} [props.value] - Controlled value (overrides React Hook Form value)
 * @param {number|string} [props.minWidth] - Minimum width of the select box
 * @param {boolean} [props.whiteBackground] - Whether to apply a white background style
 * @param {Object} [props.style] - Custom styles object
 * @param {...any} rest - Additional props passed to the TextField component
 *
 * @returns {JSX.Element} The rendered controlled select box component
 *
 * @example
 * const options = [
 *   { value: "option1", label: "Option 1" },
 *   { value: "option2", label: "Option 2", disabled: true },
 * ];
 *
 * <MiControlledSelectBox
 *   name="mySelect"
 *   formControl={control}
 *   label="Choose an option"
 *   options={options}
 *   required
 * />
 */
export function MiControlledSelectBox({
  name,
  formControl: control = {},
  label,
  fieldType = "select",
  rules,
  options,
  required,
  disabled,
  disableNone,
  handleChange,
  value,
  minWidth,
  style,
  whiteBackground,
  readOnly,
  shrink,
  ...rest
}) {
  const {
    field: { onChange, value: defaultValue, onBlur },
    fieldState: { error },
  } = useController({
    name,
    control,
    rules,
  });
  const { classes } = useStyles();
  const isShrink = !!defaultValue || shrink || !!readOnly;

  return (
    <FormControl className={classes.selectBox} error={!!error}>
      <TextField
        {...rest}
        disabled={disabled}
        value={value ?? defaultValue ?? (!disableNone ? "None" : "")}
        onChange={handleChange || onChange}
        onBlur={onBlur}
        size="small"
        select={fieldType === "select"}
        readOnly={readOnly}
        slotProps={{
          input: {
            readOnly,
          },
          select: {
            "data-testid": `mi-select-box-${toKebabCase(name || label)}`,
            readOnly,
            ...rest.SelectProps,
          },
          inputLabel: { shrink: isShrink },
        }}
        label={label}
        required={required}
        error={!!error && !readOnly}
        sx={{
          background: whiteBackground ? "white" : "transparent",
        }}
      >
        {!disableNone && (
          <MenuItem value="None" data-testid="disable-none">
            None
          </MenuItem>
        )}
        {options?.map((option, i) =>
          rest.renderOption ? (
            rest.renderOption(option)
          ) : (
            <MenuItem
              data-testid={`mi-selectbox-${toKebabCase(
                name || label
              )}-option-${i}`}
              key={option.value}
              value={option.value}
              disabled={!!option.disabled}
            >
              {option.label}
            </MenuItem>
          )
        )}
      </TextField>
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  );
}
